"use client"

import { Search, ChevronDown } from "lucide-react"
import { useState } from "react"

export interface AssessmentFilterState {
  searchQuery: string
  classFilter: string
  studentFilter: string
  courseFilter: string
  careerPathFilter: string
}

interface AssessmentFiltersProps {
  onFilterChange?: (filters: AssessmentFilterState) => void
}

export function AssessmentFilters({ onFilterChange }: AssessmentFiltersProps) {
  const [filters, setFilters] = useState<AssessmentFilterState>({
    searchQuery: "",
    classFilter: "",
    studentFilter: "",
    courseFilter: "",
    careerPathFilter: "",
  })

  const handleFilterChange = (key: keyof AssessmentFilterState, value: string) => {
    const newFilters = { ...filters, [key]: value }
    setFilters(newFilters)
    onFilterChange?.(newFilters)
  }

  return (
    <div className="w-full py-4">
      <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center">
        {/* Search */}
        <div className="flex-1 min-w-[240px]">
          <div className="flex w-full items-stretch rounded-lg h-12 bg-white border border-gray-200 shadow-sm focus-within:ring-2 focus-within:ring-[#13ecda]/50 transition-shadow">
            <div className="text-gray-400 flex items-center justify-center pl-4">
              <Search className="h-5 w-5" />
            </div>
            <input
              className="flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-gray-900 focus:outline-0 focus:ring-0 border-none bg-transparent h-full placeholder:text-gray-400 px-4 text-base font-normal leading-normal"
              placeholder="Search assessments by name..."
              value={filters.searchQuery}
              onChange={(e) => handleFilterChange("searchQuery", e.target.value)}
            />
          </div>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap gap-4 flex-1 lg:flex-none">
          {/* Class Filter */}
          <div className="relative min-w-[140px] flex-1 lg:flex-none">
            <select
              className="w-full appearance-none rounded-lg bg-white border border-gray-200 h-12 px-4 pr-10 text-gray-900 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#13ecda]/50 shadow-sm cursor-pointer"
              value={filters.classFilter}
              onChange={(e) => handleFilterChange("classFilter", e.target.value)}
            >
              <option value="">All Classes</option>
              <option value="5a">JSS 1</option>
              <option value="5b">JSS 2</option>
              <option value="6a">JSS 3</option>
              <option value="6a">SSS 1</option>
              <option value="6a">SSS 2</option>
              <option value="6a">SSS 3</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
              <ChevronDown className="h-5 w-5" />
            </div>
          </div>

          {/* Student Filter */}
          <div className="relative min-w-[140px] flex-1 lg:flex-none">
            <select
              className="w-full appearance-none rounded-lg bg-white border border-gray-200 h-12 px-4 pr-10 text-gray-900 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#13ecda]/50 shadow-sm cursor-pointer"
              value={filters.studentFilter}
              onChange={(e) => handleFilterChange("studentFilter", e.target.value)}
            >
              <option value="">All Students</option>
              <option value="active">Active Students</option>
              <option value="archived">Archived</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
              <ChevronDown className="h-5 w-5" />
            </div>
          </div>

          {/* Course Filter */}
          <div className="relative min-w-[140px] flex-1 lg:flex-none">
            <select
              className="w-full appearance-none rounded-lg bg-white border border-gray-200 h-12 px-4 pr-10 text-gray-900 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#13ecda]/50 shadow-sm cursor-pointer"
              value={filters.courseFilter}
              onChange={(e) => handleFilterChange("courseFilter", e.target.value)}
            >
              <option value="">Courses</option>
              <option value="math">Mathematics</option>
              <option value="sci">Science</option>
              <option value="art">Arts</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
              <ChevronDown className="h-5 w-5" />
            </div>
          </div>

          {/* Career Path Filter */}
          <div className="relative min-w-[160px] flex-1 lg:flex-none">
            <select
              className="w-full appearance-none rounded-lg bg-white border border-gray-200 h-12 px-4 pr-10 text-gray-900 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#13ecda]/50 shadow-sm cursor-pointer"
              value={filters.careerPathFilter}
              onChange={(e) => handleFilterChange("careerPathFilter", e.target.value)}
            >
              <option value="">Career Path</option>
              <option value="eng">Engineering</option>
              <option value="med">Medical</option>
              <option value="arts">Creative Arts</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
              <ChevronDown className="h-5 w-5" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
